import { useFrame } from '@react-three/fiber';
import type { ShaderMaterial } from 'three';
import type { Vector2 } from 'three';
import { easeOutCubic } from '../../utils/animation/easings';
import { useRef } from 'react';
import * as THREE from 'three';

interface MaterialsAnimationProps {
  materials: ShaderMaterial[];
  hoveredRef: React.RefObject<number | null>;
  selectedIndex: number | null;
  mousePos: Vector2;
  isInitialAnimationComplete: React.MutableRefObject<boolean>;
  initAnimation: number;
  scrollRef: React.RefObject<number>;
  projectsPerLoop: number;
  velocity: React.RefObject<number>;
}

export const useMaterialsAnimation = ({
  materials,
  hoveredRef,
  selectedIndex,
  mousePos,
  isInitialAnimationComplete,
  initAnimation,
  scrollRef,
  projectsPerLoop,
  velocity,
}: MaterialsAnimationProps) => {
  const transitionStates = useRef<{ [key: number]: number }>({});
  const focusScrollOffset = useRef<number | null>(null);
  const previousValues = useRef<{
    velocity: number;
    mousePos: Vector2;
    colorTransitions: { [key: number]: number };
    spiralAngles: { [key: number]: number };
  }>({
    velocity: 0,
    mousePos: new THREE.Vector2(),
    colorTransitions: {},
    spiralAngles: {},
  });

  useFrame((_, delta) => {
    if (selectedIndex !== null && focusScrollOffset.current === null) {
      focusScrollOffset.current =
        ((scrollRef.current ?? 0) * 2) % (Math.PI * 2);
    } else if (selectedIndex === null) {
      focusScrollOffset.current = null;
    }

    // Update velocity only if changed
    const currentVelocity = velocity.current;
    const velocityChanged = currentVelocity !== previousValues.current.velocity;

    materials.forEach((material, index) => {
      if (!material.uniforms) return;

      const isHovered = hoveredRef.current === index;
      const isFocused = selectedIndex === index;

      // Mettre à jour l'uniform focused
      if (material.uniforms.focused.value !== (isFocused ? 1.0 : 0.0)) {
        material.uniforms.focused.value = isFocused ? 1.0 : 0.0;
      }

      // Initialize transition state if needed
      if (!transitionStates.current[index]) {
        transitionStates.current[index] = 0;
      }

      // Color transition update
      if (!isFocused) {
        const targetValue = isHovered ? 1 : 0;
        const currentValue = transitionStates.current[index];
        const transitionSpeed = 3.0;

        if (currentValue !== targetValue) {
          const newTransitionValue = THREE.MathUtils.lerp(
            currentValue,
            targetValue,
            delta * transitionSpeed
          );

          if (
            newTransitionValue !==
            previousValues.current.colorTransitions[index]
          ) {
            transitionStates.current[index] = newTransitionValue;
            material.uniforms.colorTransition.value = newTransitionValue;
            previousValues.current.colorTransitions[index] = newTransitionValue;
          }
        }
      } else if (material.uniforms.colorTransition.value !== 1) {
        material.uniforms.colorTransition.value = 1;
        previousValues.current.colorTransitions[index] = 1;
      }

      // Spiral angle update
      const baseAngle = (index / projectsPerLoop) * Math.PI * 2;
      const animatedAngle = isInitialAnimationComplete.current
        ? selectedIndex === null
          ? (baseAngle + (((scrollRef.current ?? 0) * 2) % (Math.PI * 2))) %
            (Math.PI * 2)
          : baseAngle
        : baseAngle * easeOutCubic(initAnimation);

      // Mettre à jour l'angle de la spirale
      if (material.uniforms.spiralAngle) {
        const newSpiralAngle = animatedAngle;
        if (newSpiralAngle !== previousValues.current.spiralAngles[index]) {
          material.uniforms.spiralAngle.value = newSpiralAngle;
          previousValues.current.spiralAngles[index] = newSpiralAngle;
        }
      }

      // Mouse position update for focused items
      if (isFocused && !mousePos.equals(previousValues.current.mousePos)) {
        material.uniforms.mousePos.value.copy(mousePos);
        previousValues.current.mousePos.copy(mousePos);
      }

      // Velocity update
      if (velocityChanged) {
        material.uniforms.velocity.value = currentVelocity;
      }

      // ShaderMaterial has no needsUpdate - uniform changes apply on next render
    });

    // Update previous velocity after the loop
    if (velocityChanged && currentVelocity !== null) {
      previousValues.current.velocity = currentVelocity;
    }
  });
};
